import { type Dispatch, type SetStateAction, useState } from "react";
import {
  type SpreadsheetRegionDraft,
  updateSpreadsheetRegionLimits,
} from "./spreadsheet-region";
import { columnNumberFromName } from "./utils";

export type RegionRowLimitKey = "header_row" | "record_end_row";
export type RegionColumnLimitKey = "start_col" | "end_col";

interface UseRegionLimitFieldsParams {
  draft: SpreadsheetRegionDraft | null;
  setDraft: Dispatch<SetStateAction<SpreadsheetRegionDraft | null>>;
  onPreviewChange: (draft: SpreadsheetRegionDraft | null) => void;
  errors: string[];
}

export const useRegionLimitFields = ({
  draft,
  setDraft,
  onPreviewChange,
  errors,
}: UseRegionLimitFieldsParams) => {
  const changeLimit = (
    key: RegionRowLimitKey | RegionColumnLimitKey,
    value: number,
  ) => {
    if (!draft || !Number.isInteger(value)) return;
    const next = updateSpreadsheetRegionLimits(draft, {
      ...draft,
      [key]: value,
    });
    setDraft(next);
    onPreviewChange(next);
  };

  const [limitFieldText, setLimitFieldText] = useState<{
    header_row: string | null;
    record_end_row: string | null;
    start_col: string | null;
    end_col: string | null;
  }>({
    header_row: null,
    record_end_row: null,
    start_col: null,
    end_col: null,
  });

  const isColumnFieldValid = {
    start_col:
      limitFieldText.start_col === null ||
      columnNumberFromName(limitFieldText.start_col) !== null,
    end_col:
      limitFieldText.end_col === null ||
      columnNumberFromName(limitFieldText.end_col) !== null,
  };
  const areRowFieldsValid =
    (limitFieldText.header_row === null ||
      /^\d+$/.test(limitFieldText.header_row)) &&
    (limitFieldText.record_end_row === null ||
      /^\d+$/.test(limitFieldText.record_end_row));
  const areColumnFieldsValid =
    isColumnFieldValid.start_col && isColumnFieldValid.end_col;
  const validationErrors = [
    ...errors,
    ...(areRowFieldsValid ? [] : ["Enter valid row numbers."]),
    ...(areColumnFieldsValid
      ? []
      : ["Enter valid column letters from A through XFD."]),
  ];

  const changeRowLimit = (key: RegionRowLimitKey, value: string) => {
    setLimitFieldText((current) => ({ ...current, [key]: value }));
    if (!/^\d+$/.test(value)) return;
    changeLimit(key, Number(value));
  };

  const changeColumnLimit = (
    key: RegionColumnLimitKey,
    value: string,
  ): void => {
    setLimitFieldText((current) => ({ ...current, [key]: value }));
    const column = columnNumberFromName(value);
    if (column !== null) changeLimit(key, column);
  };

  return {
    limitFieldText,
    isColumnFieldValid,
    validationErrors,
    changeRowLimit,
    changeColumnLimit,
  };
};

export type RegionLimitFieldsState = ReturnType<typeof useRegionLimitFields>;
